import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bot, Cpu, KeyRound, MessagesSquare, RefreshCw } from 'lucide-react';
import { Card, CardBody } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { useToast } from '@/hooks/useToast';
import { relativeTime } from '@/lib/format';
import { cn } from '@/lib/cn';
import * as gastosApi from '@/api/gastos.api';
import type { ConsumoReal as Datos } from '@/api/gastos.api';

/**
 * CONSUMO REAL — nada estimado ni escrito a mano:
 *  · la IA, de lo que Anthropic registró y cobró a la clave de este sistema;
 *  · el servidor, medido cada minuto por el recolector de la máquina;
 *  · los mensajes, contados en la base.
 * Se refresca solo cada 30 segundos.
 */

/** «closetcito-postgres-1» → «Base de datos»: el servicio de Docker dicho en cristiano. */
const PIEZAS: Record<string, string> = { api: 'Programa (API)', postgres: 'Base de datos', redis: 'Cola (Redis)', web: 'Panel web', worker: 'Trabajos de fondo' };
const pieza = (contenedor: string) => {
  const servicio = contenedor.replace(/-\d+$/, '').split('-').pop() ?? contenedor;
  return PIEZAS[servicio] ?? servicio;
};

export const CLAVE_CONSUMO_REAL = ['consumo-real'] as const;

export function useConsumoReal() {
  return useQuery({ queryKey: CLAVE_CONSUMO_REAL, queryFn: gastosApi.getConsumoReal, refetchInterval: 30_000 });
}

const usd = (n: number) => `US$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const miles = (n: number) => (n >= 1e6 ? `${(n / 1e6).toFixed(2)} M` : n >= 1e3 ? `${(n / 1e3).toFixed(1)} mil` : String(n));
const moneda = (n: number | null, m: string | null) =>
  n == null ? '—' : `${m === 'EUR' ? '€' : m === 'USD' ? 'US$' : ''}${n.toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function Barra({ valor, maximo, tono = 'normal' }: { valor: number; maximo: number; tono?: 'normal' | 'alto' }) {
  const pct = maximo > 0 ? Math.min(100, (valor / maximo) * 100) : 0;
  return (
    <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
      <div className={cn('h-full rounded-full', tono === 'alto' ? 'bg-red-500' : 'bg-brand-500')} style={{ width: `${pct}%` }} />
    </div>
  );
}

function Columnas({ valores, titulo }: { valores: Array<{ k: string; v: number; detalle: string }>; titulo: string }) {
  const max = Math.max(0.0001, ...valores.map((x) => x.v));
  return (
    <div>
      <p className="mb-1 text-xs text-slate-500">{titulo}</p>
      <div className="flex h-20 items-end gap-[2px]">
        {valores.map((x) => (
          <div key={x.k} title={x.detalle} className="flex-1 rounded-t bg-brand-400 dark:bg-brand-600" style={{ height: `${Math.max(3, (x.v / max) * 100)}%` }} />
        ))}
      </div>
    </div>
  );
}

function Titulo({ icono: Icono, texto, extra }: { icono: typeof Bot; texto: string; extra?: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <p className="flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
        <Icono className="h-4 w-4 text-slate-400" /> {texto}
      </p>
      {extra}
    </div>
  );
}

function PonerClave() {
  const [clave, setClave] = useState('');
  const qc = useQueryClient();
  const toast = useToast();
  const guardar = useMutation({
    mutationFn: () => gastosApi.putClaveAdmin(clave),
    onSuccess: (r) => {
      setClave('');
      void qc.invalidateQueries({ queryKey: CLAVE_CONSUMO_REAL });
      toast.success(`Clave guardada${r.organizacion ? ` (cuenta: ${r.organizacion})` : ''}`);
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'No se pudo guardar la clave'),
  });
  return (
    <div className="space-y-2 rounded-lg border border-dashed border-surface-border p-3">
      <p className="text-sm text-slate-600 dark:text-slate-300">
        Para ver lo que Anthropic te cobra de verdad, pon la <strong>clave de administración</strong> de tu cuenta de Anthropic
        (Console → Settings → Admin keys; empieza por <code>sk-ant-admin</code>). Se comprueba con Anthropic, se guarda cifrada y no
        vuelve a mostrarse.
      </p>
      <div className="flex flex-col gap-2 sm:flex-row">
        <input
          type="password"
          autoComplete="off"
          value={clave}
          onChange={(e) => setClave(e.target.value)}
          placeholder="sk-ant-admin01-…"
          aria-label="Clave de administración de Anthropic"
          className="w-full rounded-md border border-surface-border bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900"
        />
        <Button onClick={() => guardar.mutate()} loading={guardar.isPending} disabled={clave.trim().length < 10}>
          <KeyRound className="h-4 w-4" /> Guardar
        </Button>
      </div>
    </div>
  );
}

function Ia({ ia }: { ia: Datos['ia'] }) {
  const dias = ia.dias.map((d) => ({ k: d.dia, v: d.usd || d.entrada + d.salida, detalle: `${d.dia}: ${usd(d.usd)} · ${miles(d.entrada + d.salida + d.cacheLeida + d.cacheEscrita)} tokens` }));
  return (
    <Card>
      <CardBody className="space-y-3">
        <Titulo
          icono={Bot}
          texto="La IA, según Anthropic (30 días)"
          extra={ia.estado === 'ok' && <Badge variant={ia.origenDolares === 'factura' ? 'success' : 'info'}>{ia.origenDolares === 'factura' ? 'De la factura' : 'Tokens reales × precio de lista'}</Badge>}
        />
        {ia.estado === 'sin_clave_admin' && <PonerClave />}
        {ia.estado === 'sin_clave_ia' && <p className="text-sm text-slate-500">Este sistema todavía no tiene clave de IA: no ha gastado nada en Anthropic.</p>}
        {ia.estado === 'clave_no_encontrada' && <p className="text-sm text-amber-700 dark:text-amber-300">{ia.detalle}</p>}
        {ia.estado === 'error' && <p className="text-sm text-red-600 dark:text-red-400">No se pudo leer Anthropic: {ia.detalle}</p>}
        {ia.estado === 'ok' && (
          <>
            <div className="flex flex-wrap items-end gap-x-6 gap-y-1">
              <p className="text-3xl font-bold tabular-nums text-slate-900 dark:text-slate-100">{usd(ia.total.usd)}</p>
              <p className="text-xs text-slate-500">
                {miles(ia.total.entrada)} de entrada · {miles(ia.total.salida)} de salida · {miles(ia.total.cacheLeida)} leídos de caché ·{' '}
                {miles(ia.total.cacheEscrita)} escritos en caché
              </p>
            </div>
            <Columnas valores={dias} titulo="Por día" />
            {ia.total.porModelo.length > 0 && (
              <ul className="divide-y divide-surface-border text-sm dark:divide-slate-800">
                {ia.total.porModelo.map((m) => (
                  <li key={m.modelo} className="flex justify-between py-1.5">
                    <span className="truncate text-slate-700 dark:text-slate-200">{m.modelo}</span>
                    <span className="tabular-nums text-slate-500">{miles(m.tokens)} tokens · {usd(m.usd)}</span>
                  </li>
                ))}
              </ul>
            )}
            <p className="text-xs text-slate-400">
              Clave «{ia.clave?.nombre}» ({ia.clave?.espacioPropio ? 'con su espacio de trabajo propio: los dólares son los de la factura' : 'comparte espacio de trabajo: los dólares son sus tokens reales a precio de lista'}).
              {ia.anotadoPorElSistemaUsd != null && ` El sistema anotó ${usd(ia.anotadoPorElSistemaUsd)} en el mismo período.`} Anthropic tarda unos 5 minutos en reflejar cada uso.
            </p>
          </>
        )}
      </CardBody>
    </Card>
  );
}

function Servidor({ s }: { s: Datos['servidor'] }) {
  if (s.estado === 'sin_datos' || !s.servidor || !s.proyecto) {
    return (
      <Card>
        <CardBody className="space-y-2">
          <Titulo icono={Cpu} texto="El servidor, en vivo" />
          <p className="text-sm text-slate-500">Todavía no llegan medidas del servidor (el recolector corre cada minuto).</p>
        </CardBody>
      </Card>
    );
  }
  const m = s.servidor;
  const p = s.proyecto;
  const hist = (s.historial ?? []).filter((_, i, a) => i % Math.max(1, Math.floor(a.length / 96)) === 0);
  return (
    <Card>
      <CardBody className="space-y-3">
        <Titulo
          icono={Cpu}
          texto={`El servidor, en vivo · ${m.nombre}`}
          extra={
            <span className="flex items-center gap-1 text-xs text-slate-400">
              <RefreshCw className="h-3 w-3" /> medido {relativeTime(s.medidoEn ?? null)}
              {s.estado === 'viejo' && <Badge variant="warning">sin medidas recientes</Badge>}
            </span>
          }
        />
        <div className="grid gap-3 sm:grid-cols-3">
          <div>
            <p className="text-xs text-slate-500">Este sistema usa</p>
            <p className="text-2xl font-bold tabular-nums text-slate-900 dark:text-slate-100">{p.memoriaMb} MB</p>
            <p className="text-xs text-slate-400">de memoria · CPU {p.cpuPct}%{p.discoMb != null ? ` · base ${p.discoMb} MB` : ''}</p>
          </div>
          <div>
            <p className="text-xs text-slate-500">Le toca del servidor</p>
            <p className="text-2xl font-bold tabular-nums text-slate-900 dark:text-slate-100">{moneda(p.costoMes, m.moneda)}</p>
            <p className="text-xs text-slate-400">al mes · {p.partePct}% de la memoria de la máquina</p>
          </div>
          <div className="space-y-1.5">
            <p className="text-xs text-slate-500">La máquina entera ({m.cpus} CPU)</p>
            <Barra valor={m.memoriaUsadaMb} maximo={m.memoriaMb} tono={m.memoriaUsadaMb / m.memoriaMb > 0.9 ? 'alto' : 'normal'} />
            <p className="text-xs text-slate-400">memoria {m.memoriaUsadaMb} / {m.memoriaMb} MB</p>
            <Barra valor={m.discoUsadoGb} maximo={m.discoGb} tono={m.discoUsadoGb / m.discoGb > 0.9 ? 'alto' : 'normal'} />
            <p className="text-xs text-slate-400">disco {m.discoUsadoGb} / {m.discoGb} GB · carga {m.carga1m}</p>
          </div>
        </div>
        {hist.length > 1 && <Columnas valores={hist.map((h) => ({ k: h.t, v: h.memoriaMb, detalle: `${h.t.slice(11, 16)} UTC: ${h.memoriaMb} MB · CPU ${h.cpuPct}%` }))} titulo="Memoria de este sistema, últimas 24 horas" />}
        <ul className="divide-y divide-surface-border text-sm dark:divide-slate-800">
          {(s.contenedores ?? []).map((c) => (
            <li key={c.nombre} className="flex flex-wrap justify-between gap-x-3 py-1.5">
              <span className="text-slate-700 dark:text-slate-200" title={c.nombre}>{pieza(c.nombre)}</span>
              <span className="shrink-0 tabular-nums text-slate-500">
                CPU {c.cpuPct}% · {c.memoriaMb} MB{c.memoriaLimiteMb ? ` de ${c.memoriaLimiteMb}` : ''}
              </span>
            </li>
          ))}
        </ul>
        {m.origenPrecio && <p className="text-xs text-slate-400">Precio del servidor: {moneda(m.precioMes, m.moneda)} al mes — {m.origenPrecio}.</p>}
      </CardBody>
    </Card>
  );
}

function Mensajes({ msg }: { msg: Datos['mensajes'] }) {
  const t = msg.total;
  return (
    <Card>
      <CardBody className="space-y-3">
        <Titulo icono={MessagesSquare} texto="WhatsApp y mensajes (30 días)" />
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[
            ['Entraron', t.entraron],
            ['Contestó la IA', t.ia],
            ['Contestaron personas', t.personas],
            ['Chats nuevos', t.chatsNuevos],
          ].map(([k, v]) => (
            <div key={k as string}>
              <p className="text-xs text-slate-500">{k}</p>
              <p className="text-2xl font-bold tabular-nums text-slate-900 dark:text-slate-100">{(v as number).toLocaleString('es-DO')}</p>
            </div>
          ))}
        </div>
        {msg.dias.length > 0 && <Columnas valores={msg.dias.map((d) => ({ k: d.dia, v: d.entraron + d.ia + d.personas, detalle: `${d.dia}: ${d.entraron} entraron · ${d.ia} IA · ${d.personas} personas` }))} titulo="Mensajes por día" />}
        {msg.porCanal.length > 0 && (
          <ul className="divide-y divide-surface-border text-sm dark:divide-slate-800">
            {msg.porCanal.map((c) => (
              <li key={c.canal} className="flex justify-between gap-3 py-1.5">
                <span className="truncate text-slate-700 dark:text-slate-200">{c.canal}</span>
                <span className="shrink-0 tabular-nums text-slate-500">{c.entraron} entraron · {c.ia} IA · {c.personas} personas</span>
              </li>
            ))}
          </ul>
        )}
        <p className="text-xs text-slate-400">WhatsApp no cobra por mensaje: los números van conectados directo, sin la API de pago de Meta.</p>
      </CardBody>
    </Card>
  );
}

export function ConsumoReal() {
  const { data, isLoading, isError, refetch } = useConsumoReal();
  if (isLoading) return null;
  if (isError || !data) {
    return (
      <div className="flex items-center gap-3 text-sm text-slate-500">
        No se pudo leer el consumo real.
        <Button variant="outline" size="sm" onClick={() => void refetch()}>
          Reintentar
        </Button>
      </div>
    );
  }
  return (
    <section className="space-y-4" aria-label="Consumo real">
      <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100">Consumo real</h2>
      <Ia ia={data.ia} />
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <Servidor s={data.servidor} />
        <Mensajes msg={data.mensajes} />
      </div>
    </section>
  );
}
